import React, { useState, useEffect } from "react";
import {
	Table,
	Row,
	Col,
	Card,
	notification,
	Select,
	Spin,
	Tag,
	Badge,
	Tooltip,
	DatePicker,
} from "antd";

import {
	AppstoreAddOutlined,
	EyeOutlined,
	ScheduleOutlined,
} from "@ant-design/icons";

import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import DefaultLayout from "../../components/layout/DefaultLayout";
import Header from "../../components/header";
import dashboardReportService from "../../services/dashboardReport";
// import FormListComponent from "../../components/serviceDeskFormList";
import FormListComponent from "../../components/formList";
import { useMediaQuery } from "react-responsive";
import userService from "../../services/user";

import UserSearchSelect from "../../components/userSearchSelect";

const { Option } = Select;
const { RangePicker } = DatePicker;

const DashboardStatusReport = () => {
	const navigate = useNavigate();
	const location = useLocation();

	const { currentUser } = useSelector((state) => state.user);
	const dbLocalKey = currentUser?.user_id
		? `dashboardReportLocal_${currentUser.user_id}`
		: null;
	const DB_TTL_MS = 24 * 60 * 60 * 1000;

	// Synchronously get saved state if possible
	const getSavedState = () => {
		if (!dbLocalKey) return null;
		try {
			const saved = localStorage.getItem(dbLocalKey);
			if (saved) {
				const parsed = JSON.parse(saved);
				if (parsed.savedAt && Date.now() - parsed.savedAt < DB_TTL_MS) {
					return parsed;
				}
				localStorage.removeItem(dbLocalKey);
			}
		} catch (e) {
			console.warn("Could not parse dashboard state", e);
		}
		return null;
	};

	const savedState = getSavedState();
	const savedFilters = savedState?.filters || {};

	const initialStatus =
		location.state?.statusFilter ||
		savedFilters.statusFilter ||
		"Processing/Pending";
	const initialForm =
		location.state?.selectedForm || savedFilters.selectedForm || null;

	const [currentPage, setCurrentPage] = useState(savedState?.page || 1);
	const [itemsPerPage, setItemsPerPage] = useState(10);
	const [totalDataCount, setTotalDataCount] = useState(0);
	const [totalDisplay, setTotalDisplay] = useState("");
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState([]);

	const [selectedForm, setSelectedForm] = useState(initialForm);
	const [statusFilter, setStatusFilter] = useState(initialStatus);
	const [users, setUsers] = useState(null);

	// NEW FILTERS
	const [selectedUser, setSelectedUser] = useState(
		savedFilters.selectedUser || [],
	);
	const [userType, setUserType] = useState(savedFilters.userType || 1);
	const [fromDate, setFromDate] = useState(savedFilters.fromDate || null);
	const [toDate, setToDate] = useState(savedFilters.toDate || null);

	const [isStateRestored, setIsStateRestored] = useState(!!dbLocalKey);

	// Handle async redux populating currentUser
	useEffect(() => {
		if (dbLocalKey && !isStateRestored) {
			const state = getSavedState();
			if (state) {
				const f = state.filters || {};
				if (f.statusFilter) setStatusFilter(f.statusFilter);
				if (f.selectedForm) setSelectedForm(f.selectedForm);
				if (f.selectedUser) setSelectedUser(f.selectedUser);
				if (f.userType) setUserType(f.userType);
				if (f.fromDate) setFromDate(f.fromDate);
				if (f.toDate) setToDate(f.toDate);
				setCurrentPage(state.page || 1);
			}
			setIsStateRestored(true);
		}
	}, [dbLocalKey, isStateRestored]);

	// Save state when filters change
	useEffect(() => {
		if (!dbLocalKey || !isStateRestored) return;
		try {
			const filters = {
				statusFilter,
				selectedForm,
				selectedUser,
				userType,
				fromDate,
				toDate,
			};
			localStorage.setItem(
				dbLocalKey,
				JSON.stringify({ filters, page: currentPage, savedAt: Date.now() }),
			);
		} catch (e) {
			console.warn("Could not save dashboard state", e);
		}
	}, [
		statusFilter,
		selectedForm,
		selectedUser,
		userType,
		fromDate,
		toDate,
		currentPage,
		dbLocalKey,
		isStateRestored,
	]);

	const taskStatus = [
		"Approved",
		"Disapproved",
		"Return",
		"Processing/Pending",
	];

	const getUsers = async () => {
		let response;
		response = await userService.getUsers();
		setUsers(response.data);
	};

	// Fetch data
	const getData = async () => {
		if (!statusFilter) return;

		setLoading(true);

		try {
			const response = await dashboardReportService.getDashboardStatusReport(
				currentPage,
				statusFilter,
				selectedForm,
				selectedUser.length > 0 ? selectedUser : null,
				fromDate,
				toDate,
				userType,
			);

			if (response.success) {
				const items = response.items || [];
				const meta = response.meta || {};

				setData(
					items.map((item, index) => ({
						...item,
						__unique_key: `${item.key || index}-${index}`,
					})),
				);

				const perPage = Number(meta.per_page) || items.length || 10;
				setItemsPerPage(perPage);

				const current = Number(meta.current_page) || currentPage || 1;
				if (meta.current_page) setCurrentPage(current);

				const total =
					meta.total !== undefined ? Number(meta.total) : items.length;

				setTotalDataCount(total);
				setTotalDisplay(meta.total || String(total));
			} else {
				setData([]);
				setTotalDataCount(0);
			}
		} catch (error) {
			notification.error({
				message: "Error",
				description: error?.response?.data?.message || "Something went wrong",
			});
			setData([]);
			setTotalDataCount(0);
		} finally {
			setLoading(false);
		}
	};

	// Fetch users on mount
	useEffect(() => {
		getUsers();
	}, []);

	// Fetch data when filters or page change
	useEffect(() => {
		if (!dbLocalKey || isStateRestored) {
			getData();
		}
	}, [
		statusFilter,
		selectedForm,
		selectedUser,
		fromDate,
		toDate,
		userType,
		currentPage,
		isStateRestored,
		dbLocalKey,
	]);

	const columns = [
		{
			title: "Form",
			dataIndex: "form_name",
			width: "15%",
		},
		{
			title: "Sequence No",
			dataIndex: "sequence_no",
			width: "15%",
		},
		{
			title: "Request Title",
			dataIndex: "request_title",
			width: "40%",
		},
		{
			title: "Status",
			dataIndex: "status",
			width: "10%",
			render: (status) => (
				<Tag
					color={
						(status === "Processing" && "blue") ||
						(status === "Approved" && "green") ||
						(status === "Return" && "red") ||
						(status === "Pending" && "orange")
					}
				>
					{status}
				</Tag>
			),
			fixed: useMediaQuery({ minWidth: 768 }) ? "right" : null,
		},
		{
			title: "Logs",
			key: "reason",
			width: "10%",
			render: (_, record) => (
				<Tooltip title='Activity Log'>
					<span
						onClick={() =>
							navigate("/activity-log", {
								state: {
									form_id: record.form_id,
									key: record.key,
								},
							})
						}
						style={{ cursor: "pointer" }}
					>
						<ScheduleOutlined style={{ color: "#1677ff" }} />
					</span>
				</Tooltip>
			),
		},
		{
			title: "Action",
			width: "10%",
			render: (_, record) => (
				<Link to={`/${record.form_slug}/details/${record.key}`}>
					<EyeOutlined />
				</Link>
			),
		},
	];

	return (
		<DefaultLayout>
			<Row gutter={[24, 24]}>
				<Col span={24}>
					<Header icon={<AppstoreAddOutlined />} title='Status Overview' />
				</Col>
			</Row>

			<Card>
				<Row gutter={24} style={{ marginBottom: 16 }}>
					<Col span={4}>
						<Select
							showSearch
							size='large'
							placeholder='Status'
							value={statusFilter}
							onChange={(value) => {
								setStatusFilter(value);
								setCurrentPage(1);
							}}
							allowClear
							style={{ width: "100%" }}
						>
							{taskStatus.map((status) => (
								<Option key={status} value={status}>
									{status}
								</Option>
							))}
						</Select>
					</Col>

					<Col span={4}>
						<FormListComponent
							onChange={(value) => {
								setSelectedForm(value);
								setCurrentPage(1);
							}}
							selectedFormId={selectedForm}
						/>
					</Col>

					<Col span={4}>
						<Select
							size='large'
							placeholder='User Type'
							value={userType}
							onChange={(value) => {
								setUserType(value);
								setCurrentPage(1);
							}}
							style={{ width: "100%" }}
						>
							<Option value={1}>Initiator</Option>
							<Option value={2}>Response By</Option>
						</Select>
					</Col>

					{/* <Col span={4}>
		<Select
			mode="multiple"
			showSearch
			size="large"
			placeholder="Select Initiator"
			value={selectedUser}
			onChange={(value) => {
				setSelectedUser(value);
				setCurrentPage(1);
			}}
			allowClear
			style={{ width: "100%" }}
			filterOption={(input, option) =>
				String(option?.children ?? "").toLowerCase().includes(input.toLowerCase())
			}
		>
			{users?.map((user) => (
				<Option key={user.id} value={user.id}>
					{user.name}
				</Option>
			))}
		</Select>
	</Col> */}
					<Col span={4}>
						<UserSearchSelect
							mode='multiple'
							value={selectedUser}
							onChange={(value) => {
								setSelectedUser(value);
								setCurrentPage(1);
							}}
							placeholder='Select Users'
							size='large'
							initialUsers={
								users ? users.filter((u) => selectedUser.includes(u.id)) : []
							}
						/>
					</Col>
					<Col span={4}>
						<RangePicker
							size='large'
							style={{ width: "100%" }}
							value={
								fromDate || toDate
									? [
											fromDate ? moment(fromDate) : null,
											toDate ? moment(toDate) : null,
										]
									: undefined
							}
							onChange={(dates, dateStrings) => {
								setFromDate(dateStrings[0] || null);
								setToDate(dateStrings[1] || null);
								setCurrentPage(1);
							}}
						/>
					</Col>

					<Col span={4}>
						<Badge
							count={totalDisplay}
							overflowCount={999}
							style={{ width: "100%" }}
						>
							<Card title='Total Records' style={{ border: "none" }} />
						</Badge>
					</Col>
				</Row>
			</Card>

			<Card>
				<Spin spinning={loading}>
					<Table
						rowKey='__unique_key'
						columns={columns}
						dataSource={data}
						pagination={{
							current: currentPage,
							pageSize: itemsPerPage,
							total: totalDataCount,
							onChange: (page) => setCurrentPage(page),
						}}
						scroll={{ y: 400 }}
					/>
				</Spin>
			</Card>
		</DefaultLayout>
	);
};

export default DashboardStatusReport;
